// src/hooks/useNotifications.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

// =====================================================
// QUERIES
// =====================================================

/**
 * Get notifications for current user
 */
export const useNotifications = (userId, options = {}) => {
  return useQuery({
    queryKey: ['notifications', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
    ...options
  });
};

/**
 * Get unread notification count
 * Uses optimized RPC function to bypass RLS for performance
 */
export const useUnreadNotificationCount = (userId, options = {}) => {
  return useQuery({
    queryKey: ['notifications', 'unread', userId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_my_unread_notification_count');

      if (error) throw error;
      return data || 0;
    },
    enabled: !!userId,
    ...options
  });
};

/**
 * Get notifications by type
 */
export const useNotificationsByType = (userId, type, options = {}) => {
  return useQuery({
    queryKey: ['notifications', userId, type],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .eq('type', type)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      return data || [];
    },
    enabled: !!userId && !!type,
    ...options
  });
};

/**
 * Get high priority notifications
 */
export const usePriorityNotifications = (userId, options = {}) => {
  return useQuery({
    queryKey: ['notifications', 'priority', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .eq('priority', 'high')
        .eq('read', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
    ...options
  });
};

// =====================================================
// MUTATIONS
// =====================================================

/**
 * Create a notification
 */
export const useCreateNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationData) => {
      const { data, error } = await supabase
        .from('notifications')
        .insert(notificationData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error) => {
      logger.error('Create notification error', { message: error.message });
    }
  });
};

/**
 * Bulk create notifications (e.g., for announcements to multiple parents)
 */
export const useBulkCreateNotifications = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationsData) => {
      const { data, error } = await supabase
        .from('notifications')
        .insert(notificationsData)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      logger.debug('Notifications created', { count: data.length });
    },
    onError: (error) => {
      logger.error('Bulk create notifications error', { message: error.message });
    }
  });
};

/**
 * Mark notification as read
 */
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId) => {
      const { data, error } = await supabase
        .from('notifications')
        .update({ read: true, read_at: new Date().toISOString() })
        .eq('id', notificationId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error) => {
      logger.error('Mark as read error', { message: error.message });
    }
  });
};

/**
 * Mark all notifications as read for a user
 */
export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId) => {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true, read_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('read', false);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('Wszystkie powiadomienia oznaczone jako przeczytane');
    },
    onError: (error) => {
      logger.error('Mark all as read error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Delete a notification
 */
export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId) => {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error) => {
      logger.error('Delete notification error', { message: error.message });
      toast.error(`Failed to delete: ${error.message}`);
    }
  });
};

/**
 * Clear old notifications (older than 30 days)
 */
export const useClearOldNotifications = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId) => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', userId)
        .lt('created_at', thirtyDaysAgo.toISOString());

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('Old notifications cleared');
    },
    onError: (error) => {
      logger.error('Clear old notifications error', { message: error.message });
      toast.error(`Failed: ${error.message}`);
    }
  });
};
